import { useCallback, useEffect, useMemo, useState } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { refusalProse } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/refusalProse';
import {
  useGetRosterCohortsQuery,
  type AdminCohort,
  type AdminRoster,
} from '@backrs_ui/grow-ui/src/redux/institutionAdmin';

import { Typography } from '@mui/material';

import { COHORT_HEADERS, COHORT_TABLE_ARIA, CohortRow, type CohortDoors } from './CohortRow';
import { RostersAdminTable, usePagedRows } from './RostersAdminTable';

const strings = appStrings.modules.rostersAdmin;
const { cohorts: cohortStrings } = appStrings.modules.coachingDashboard.institutionAdmin;
const institutionAdminStrings = appStrings.modules.coachingDashboard.institutionAdmin;

type Loaded = Readonly<{
  roster: AdminRoster;
  rows: readonly AdminCohort[];
  isFetching: boolean;
  isError: boolean;
  errorMessage: string | null;
  /** Rows the server sent and the client could not decode. They leave no other trace here. */
  dropped: number;
}>;

type ReportFn = (rosterId: number, loaded: Loaded) => void;

/**
 * Renders nothing. The cohorts read is roster-scoped, so one flat paginated list has to be
 * assembled from every in-scope roster's read before a page of it can be cut.
 */
const RosterCohortsLoader = ({ roster, report }: { roster: AdminRoster; report: ReportFn }) => {
  const cohorts = useGetRosterCohortsQuery({ rosterId: roster.id });
  const rows = cohorts.data?.data;
  const { isFetching, isError, error } = cohorts;

  const reported = useMemo(
    () => ({
      roster,
      rows: rows ?? [],
      isFetching,
      isError,
      errorMessage: refusalProse(error),
      dropped: cohorts.data?.dropped ?? 0,
    }),
    [cohorts.data?.dropped, error, isError, isFetching, roster, rows],
  );

  // An effect, not a render-time call: reporting during render updates the parent while a
  // child is rendering, which React refuses.
  useEffect(() => report(roster.id, reported), [report, reported, roster.id]);

  return null;
};

type CohortsTabProps = Readonly<{
  rosters: readonly AdminRoster[];
  isRostersLoading: boolean;
  isRostersError: boolean;
  /** A filter is narrowing the rosters this tab assembles from, or the cohorts themselves. */
  isNarrowed?: boolean;
  search?: string;
  doors: CohortDoors;
}>;

export const CohortsTab = ({
  rosters,
  isRostersLoading,
  isRostersError,
  isNarrowed = false,
  search = '',
  doors,
}: CohortsTabProps) => {
  const [loaded, setLoaded] = useState<Record<number, Loaded>>({});

  const report = useCallback<ReportFn>((rosterId, next) => {
    setLoaded((prev) => (prev[rosterId] === next ? prev : { ...prev, [rosterId]: next }));
  }, []);

  const inScope = useMemo(
    () => rosters.map((roster) => loaded[roster.id]).filter((entry): entry is Loaded => !!entry),
    [loaded, rosters],
  );

  const merged = useMemo(() => {
    const rows = inScope.flatMap((entry) =>
      entry.rows.map((cohort) => ({ roster: entry.roster, cohort })),
    );
    const needle = search.trim().toLowerCase();

    if (needle === '') return rows;

    return rows.filter(({ cohort }) => (cohort.name ?? '').toLowerCase().includes(needle));
  }, [inScope, search]);

  const dropped = inScope.reduce((total, entry) => total + entry.dropped, 0);
  const isAnyFetching = inScope.some((entry) => entry.isFetching) || isRostersLoading;
  const isAnyError = inScope.some((entry) => entry.isError) || isRostersError;
  const firstErrorMessage = inScope.find((entry) => entry.errorMessage !== null)?.errorMessage;

  const paging = usePagedRows(merged);

  const loaders = rosters.map((roster) => (
    <RosterCohortsLoader key={roster.id} roster={roster} report={report} />
  ));

  const notice = (() => {
    // A failed ROSTERS read is not a failed cohorts read: this tab assembles from rosters, so
    // naming the real cause beats reporting the symptom on the wrong list.
    if (isRostersError) return strings.cohorts.rostersUnavailable;
    if (isAnyError) return firstErrorMessage ?? cohortStrings.error;
    if (isAnyFetching && merged.length === 0) return strings.cohorts.loading;
    if (merged.length === 0) {
      return isNarrowed ? strings.cohorts.narrowedEmpty : strings.cohorts.empty;
    }

    return null;
  })();

  if (notice !== null) {
    return (
      <>
        {loaders}
        <Typography
          id="rosters-admin-cohorts-tab-state"
          variant="text-sm-regular"
          role={isAnyError ? 'alert' : 'status'}
        >
          {notice}
        </Typography>
      </>
    );
  }

  return (
    <>
      {loaders}
      {dropped > 0 ? (
        // The pagination count below vouches for this list, so a silent drop makes the page
        // corroborate data it lost.
        <Typography id="rosters-admin-cohorts-incomplete" variant="text-sm-regular" role="status">
          {institutionAdminStrings.incompleteList(dropped)}
        </Typography>
      ) : null}
      <RostersAdminTable
        paginationId="rosters-admin-cohorts-pagination"
        headers={COHORT_HEADERS}
        ariaLabel={COHORT_TABLE_ARIA}
        paging={paging}
      >
        {paging.visible.map(({ roster, cohort }) => (
          <CohortRow key={cohort.id} roster={roster} cohort={cohort} doors={doors} />
        ))}
      </RostersAdminTable>
    </>
  );
};
